import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { PipelineRun, Blog, ContentSourceType, SelectedFile, ProcessingStep, ContentGenSettings } from '../../types';

@Component({
  standalone: false,
  selector: 'mkt-landing',
  template: `
    <!-- Hero Create Area -->
    <div class="mkt-landing__hero">
      <h3 class="mkt-landing__hero-title">
        <i class="fa-solid fa-sparkles"></i> Create Content
      </h3>
      <div class="mkt-landing__hero-form">
        <!-- Source type selector (always shown first) -->
        <div class="mkt-landing__hero-actions" style="margin-bottom: 10px;">
          <select class="mkt-landing__select"
                  [ngModel]="newSourceType"
                  (ngModelChange)="onSourceTypeChange($event)"
                  [disabled]="isProcessing">
            <option value="">Select source type...</option>
            <option *ngFor="let t of contentSourceTypes" [value]="t.Name">{{ t.Name }}</option>
          </select>
        </div>

        <!-- Input area: changes based on source type -->

        <!-- DEFAULT / Podcast Transcript / Webinar Recording: Textarea -->
        <ng-container *ngIf="showTextarea">
          <div *ngIf="isPodcastOrWebinar" class="mkt-landing__tabs">
            <button class="mkt-landing__tab" [class.mkt-landing__tab--active]="activeTab === 'paste'" (click)="activeTab = 'paste'">
              <i class="fa-solid fa-paste"></i> Paste Transcript
            </button>
            <button class="mkt-landing__tab" [class.mkt-landing__tab--active]="activeTab === 'upload'" (click)="activeTab = 'upload'">
              <i class="fa-solid fa-microphone"></i> Upload Audio
            </button>
          </div>

          <ng-container *ngIf="activeTab === 'paste' || !isPodcastOrWebinar">
            <textarea
              class="mkt-landing__textarea"
              [ngModel]="newSourceContent"
              (ngModelChange)="newSourceContentChange.emit($event)"
              (keydown)="onKeydown($event)"
              [placeholder]="textareaPlaceholder"
              rows="4"
              [disabled]="isProcessing">
            </textarea>
          </ng-container>

          <!-- Audio upload tab (stub - coming soon) -->
          <div *ngIf="activeTab === 'upload' && isPodcastOrWebinar" class="mkt-landing__coming-soon">
            <i class="fa-solid fa-microphone-lines"></i>
            <p>Audio upload &amp; automatic transcription coming soon.</p>
            <p class="mkt-landing__coming-soon-hint">For now, use the "Paste Transcript" tab to paste your transcript text.</p>
          </div>

          <!-- Speaker field for podcast/webinar -->
          <div *ngIf="isPodcastOrWebinar && activeTab === 'paste'" class="mkt-landing__field-row">
            <input class="mkt-landing__input"
                   [ngModel]="newSourceSpeaker"
                   (ngModelChange)="newSourceSpeakerChange.emit($event)"
                   placeholder="Speaker name (optional)"
                   [disabled]="isProcessing" />
          </div>
        </ng-container>

        <!-- URL / RSS Feed: URL input -->
        <ng-container *ngIf="showUrlInput">
          <input class="mkt-landing__input mkt-landing__input--url"
                 type="url"
                 [ngModel]="newSourceUrl"
                 (ngModelChange)="newSourceUrlChange.emit($event)"
                 (keydown)="onKeydown($event)"
                 placeholder="https://example.com/article..."
                 [disabled]="isProcessing" />
        </ng-container>

        <!-- Document: File drop zone -->
        <ng-container *ngIf="showFileUpload">
          <div class="mkt-landing__dropzone"
               [class.mkt-landing__dropzone--dragover]="isDragOver"
               [class.mkt-landing__dropzone--has-file]="selectedFile"
               (drop)="onFileDrop($event)"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave($event)"
               (click)="fileInput.click()">
            <input #fileInput type="file" style="display:none"
                   [accept]="fileAccept"
                   (change)="onFileSelected($event)" />
            <ng-container *ngIf="!selectedFile">
              <i class="fa-solid fa-cloud-arrow-up"></i>
              <p>Drag &amp; drop a file here, or click to browse</p>
              <p class="mkt-landing__dropzone-hint">Supports PDF, DOCX, TXT</p>
            </ng-container>
            <ng-container *ngIf="selectedFile">
              <div class="mkt-landing__file-info">
                <i class="fa-solid fa-file-lines"></i>
                <div>
                  <div class="mkt-landing__file-name">{{ selectedFile.name }}</div>
                  <div class="mkt-landing__file-size">{{ formatFileSize(selectedFile.size) }}</div>
                </div>
                <button class="mkt-landing__file-remove" (click)="removeFile($event)">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </ng-container>
          </div>
        </ng-container>

        <!-- Optional title field for URL/Document types -->
        <div *ngIf="showUrlInput || showFileUpload" class="mkt-landing__field-row">
          <input class="mkt-landing__input"
                 [ngModel]="newSourceContent"
                 (ngModelChange)="newSourceContentChange.emit($event)"
                 placeholder="Title (optional — will be auto-detected)"
                 [disabled]="isProcessing" />
        </div>

        <!-- Generate button + advanced toggle -->
        <div class="mkt-landing__hero-actions">
          <button class="mkt-landing__generate-btn"
                  [disabled]="!canGenerate || isProcessing"
                  (click)="createContent.emit()">
            <i class="fa-solid" [ngClass]="isProcessing ? 'fa-spinner fa-spin' : 'fa-bolt'"></i>
            {{ isProcessing ? 'Generating...' : 'Generate Content' }}
            <kbd *ngIf="!isProcessing" class="mkt-landing__kbd">&#8984;&#9166;</kbd>
          </button>
          <button *ngIf="isDeveloperMode && !isProcessing" class="mkt-landing__simulate-btn"
                  (click)="simulateRun.emit()" title="Simulate a pipeline run (dev mode)">
            <i class="fa-solid fa-flask"></i> Simulate
          </button>
          <button class="mkt-landing__advanced-toggle" (click)="advancedOpen = !advancedOpen"
                  [class.mkt-landing__advanced-toggle--open]="advancedOpen">
            <i class="fa-solid fa-sliders"></i>
            {{ advancedOpen ? 'Hide' : 'Settings' }}
            <i class="fa-solid" [ngClass]="advancedOpen ? 'fa-chevron-up' : 'fa-chevron-down'" style="font-size:0.65rem;"></i>
          </button>
        </div>

        <!-- Advanced Settings -->
        <div class="mkt-landing__advanced" *ngIf="advancedOpen">
          <div class="mkt-landing__advanced-row">
            <label>Posts</label>
            <select [ngModel]="genSettings.numPosts" (ngModelChange)="updateSetting('numPosts', +$event)" [disabled]="isProcessing">
              <option *ngFor="let n of [1,2,3,4,5]" [value]="n">{{ n }}</option>
            </select>
          </div>
          <div class="mkt-landing__advanced-row">
            <label>Style</label>
            <select [ngModel]="genSettings.contentStyle" (ngModelChange)="updateSetting('contentStyle', $event)" [disabled]="isProcessing">
              <option *ngFor="let s of contentStyles" [value]="s">{{ s }}</option>
            </select>
          </div>
          <div class="mkt-landing__advanced-row">
            <label>Word Count</label>
            <select [ngModel]="genSettings.targetWordCount" (ngModelChange)="updateSetting('targetWordCount', +$event)" [disabled]="isProcessing">
              <option *ngFor="let w of wordCounts" [value]="w.value">{{ w.label }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Processing pipeline -->
      <div class="mkt-landing__pipeline" *ngIf="processingSteps.length > 0">
        <div *ngFor="let step of processingSteps; let last = last"
             class="mkt-pipeline-step"
             [class.mkt-pipeline-step--done]="step.status === 'done'"
             [class.mkt-pipeline-step--active]="step.status === 'active'"
             [class.mkt-pipeline-step--pending]="step.status === 'pending'">
          <div class="mkt-pipeline-step__icon">
            <i *ngIf="step.status === 'done'" class="fa-solid fa-circle-check"></i>
            <i *ngIf="step.status === 'active'" class="fa-solid fa-spinner fa-spin"></i>
            <i *ngIf="step.status === 'pending'" class="fa-regular fa-circle"></i>
          </div>
          <span class="mkt-pipeline-step__label">{{ step.label }}</span>
        </div>
        <!-- Elapsed time -->
        <div class="mkt-pipeline-elapsed" *ngIf="processingStatus">
          {{ processingStatus }}
        </div>
      </div>

      <!-- Simple status fallback (error only, or when no steps) -->
      <div class="mkt-landing__status" *ngIf="processingError && processingSteps.length === 0">
        <div class="mkt-landing__status-bar mkt-landing__status-bar--error">
          <div class="mkt-landing__status-icon">
            <i class="fa-solid fa-circle-exclamation"></i>
          </div>
          <div class="mkt-landing__status-text">
            <span class="mkt-landing__status-message">{{ processingStatus }}</span>
            <span class="mkt-landing__status-error">{{ processingError }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI Bar -->
    <div class="mkt-landing__kpis">
      <div class="mkt-kpi mkt-kpi--sources">
        <div class="mkt-kpi__value">{{ kpiSourcesIngested }}</div>
        <div class="mkt-kpi__label">Sources Ingested</div>
      </div>
      <div class="mkt-kpi mkt-kpi--generated">
        <div class="mkt-kpi__value">{{ kpiPostsGenerated }}</div>
        <div class="mkt-kpi__label">Pipeline Generated</div>
      </div>
      <div class="mkt-kpi mkt-kpi--review mkt-kpi--clickable" (click)="openReviewQueue.emit()">
        <div class="mkt-kpi__value">{{ kpiAwaitingReview }}</div>
        <div class="mkt-kpi__label">Awaiting Review</div>
      </div>
      <div class="mkt-kpi mkt-kpi--published">
        <div class="mkt-kpi__value">{{ kpiPublished }}</div>
        <div class="mkt-kpi__label">Published</div>
      </div>
      <div class="mkt-kpi mkt-kpi--total">
        <div class="mkt-kpi__value">{{ kpiTotalPosts }}</div>
        <div class="mkt-kpi__label">Total Posts</div>
      </div>
    </div>

    <!-- Pipeline Runs -->
    <div class="mkt-landing__runs">
      <h3 class="mkt-landing__section-title">Recent Pipeline Runs</h3>
      <div class="mkt-landing__run-list">
        <mkt-run-card
          *ngFor="let run of pipelineRuns; let i = index"
          [run]="run"
          [contentSourceTypes]="contentSourceTypes"
          (selectRun)="selectRun.emit(i)">
        </mkt-run-card>
        <div class="mkt-landing__empty" *ngIf="pipelineRuns.length === 0">
          <i class="fa-solid fa-inbox"></i>
          <p>No pipeline runs yet. Paste content above to get started.</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      padding: 20px;
      overflow-y: auto;
      overflow-x: hidden;
      flex: 1;
      box-sizing: border-box;
    }

    .mkt-landing__hero {
      position: relative;
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 20px;
      overflow: hidden;
    }
    .mkt-landing__hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 80% 60% at 15% 40%, color-mix(in srgb, var(--mj-brand-primary) 7%, transparent) 0%, transparent 100%),
        radial-gradient(ellipse 60% 80% at 85% 20%, color-mix(in srgb, var(--mj-color-accent-500, #38a9d9) 6%, transparent) 0%, transparent 100%),
        radial-gradient(ellipse 50% 70% at 50% 90%, color-mix(in srgb, var(--mj-color-tertiary-500, #06b6d4) 4%, transparent) 0%, transparent 100%);
      pointer-events: none;
      z-index: 0;
    }
    .mkt-landing__hero > * {
      position: relative;
      z-index: 1;
    }
    .mkt-landing__hero::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--mj-brand-primary), var(--mj-color-accent-500, #38a9d9), var(--mj-color-tertiary-500, #06b6d4));
      border-radius: 12px 12px 0 0;
      z-index: 1;
    }
    .mkt-landing__hero-title {
      margin: 0 0 12px;
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--mj-text-primary);
    }
    .mkt-landing__hero-title i {
      color: var(--mj-brand-primary);
    }
    .mkt-landing__textarea {
      width: 100%;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      padding: 10px 12px;
      font-size: 0.9rem;
      font-family: inherit;
      resize: vertical;
      box-sizing: border-box;
      transition: border-color 0.15s;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
    }
    .mkt-landing__textarea:focus {
      outline: none;
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 12%, transparent);
    }
    .mkt-landing__textarea:disabled {
      background: var(--mj-bg-page);
    }
    .mkt-landing__hero-actions {
      display: flex;
      gap: 10px;
      margin-top: 10px;
      align-items: center;
    }
    .mkt-landing__select {
      padding: 8px 12px;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      font-size: 0.85rem;
      font-family: inherit;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
      min-width: 180px;
    }
    .mkt-landing__select:disabled {
      background: var(--mj-bg-page);
    }
    .mkt-landing__input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      font-size: 0.9rem;
      font-family: inherit;
      box-sizing: border-box;
      transition: border-color 0.15s;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
    }
    .mkt-landing__input:focus {
      outline: none;
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 12%, transparent);
    }
    .mkt-landing__input:disabled {
      background: var(--mj-bg-page);
    }
    .mkt-landing__input--url {
      font-family: monospace;
    }
    .mkt-landing__field-row {
      margin-top: 8px;
    }
    .mkt-landing__generate-btn {
      padding: 8px 20px;
      border: none;
      border-radius: 6px;
      background: var(--mj-brand-primary);
      color: var(--mj-brand-on-primary);
      font-size: 0.9rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: background 0.15s;
      white-space: nowrap;
    }
    .mkt-landing__generate-btn:hover:not(:disabled) {
      background: var(--mj-brand-primary-hover);
    }
    .mkt-landing__generate-btn:disabled {
      background: var(--mj-text-disabled);
      cursor: not-allowed;
    }

    /* Tabs */
    .mkt-landing__tabs {
      display: flex;
      gap: 0;
      margin-bottom: 8px;
    }
    .mkt-landing__tab {
      padding: 6px 14px;
      border: 1px solid var(--mj-border-default);
      background: var(--mj-bg-page);
      font-size: 0.82rem;
      font-family: inherit;
      cursor: pointer;
      color: var(--mj-text-muted);
      transition: all 0.15s;
    }
    .mkt-landing__tab:first-child {
      border-radius: 6px 0 0 6px;
    }
    .mkt-landing__tab:last-child {
      border-radius: 0 6px 6px 0;
      border-left: none;
    }
    .mkt-landing__tab--active {
      background: var(--mj-brand-primary);
      color: var(--mj-brand-on-primary);
      border-color: var(--mj-brand-primary);
    }
    .mkt-landing__tab i {
      margin-right: 4px;
    }

    /* Coming soon */
    .mkt-landing__coming-soon {
      text-align: center;
      padding: 30px 20px;
      background: var(--mj-bg-page);
      border: 1px dashed var(--mj-border-default);
      border-radius: 6px;
      color: var(--mj-text-disabled);
    }
    .mkt-landing__coming-soon i {
      font-size: 2rem;
      margin-bottom: 8px;
      display: block;
    }
    .mkt-landing__coming-soon p {
      margin: 4px 0;
      font-size: 0.85rem;
    }
    .mkt-landing__coming-soon-hint {
      font-size: 0.78rem !important;
      color: var(--mj-text-disabled);
    }

    /* Drop zone */
    .mkt-landing__dropzone {
      border: 2px dashed var(--mj-border-default);
      border-radius: 8px;
      padding: 30px 20px;
      text-align: center;
      cursor: pointer;
      transition: all 0.15s;
      color: var(--mj-text-disabled);
      background: var(--mj-bg-surface-sunken);
    }
    .mkt-landing__dropzone:hover {
      border-color: var(--mj-brand-primary);
      background: color-mix(in srgb, var(--mj-brand-primary) 5%, var(--mj-bg-surface));
    }
    .mkt-landing__dropzone--dragover {
      border-color: var(--mj-brand-primary);
      background: var(--mj-status-info-bg);
    }
    .mkt-landing__dropzone--has-file {
      border-style: solid;
      border-color: var(--mj-status-success);
      background: var(--mj-status-success-bg);
      cursor: default;
    }
    .mkt-landing__dropzone i {
      font-size: 2rem;
      margin-bottom: 8px;
      display: block;
    }
    .mkt-landing__dropzone p {
      margin: 4px 0;
      font-size: 0.85rem;
    }
    .mkt-landing__dropzone-hint {
      font-size: 0.78rem !important;
      color: var(--mj-text-disabled);
    }
    .mkt-landing__file-info {
      display: flex;
      align-items: center;
      gap: 12px;
      color: var(--mj-text-primary);
    }
    .mkt-landing__file-info > i {
      font-size: 1.5rem;
      color: var(--mj-brand-primary);
      margin-bottom: 0;
    }
    .mkt-landing__file-name {
      font-weight: 600;
      font-size: 0.9rem;
    }
    .mkt-landing__file-size {
      font-size: 0.78rem;
      color: var(--mj-text-muted);
    }
    .mkt-landing__file-remove {
      margin-left: auto;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-disabled);
      font-size: 1rem;
      padding: 4px 8px;
      border-radius: 4px;
      transition: all 0.15s;
    }
    .mkt-landing__file-remove:hover {
      color: var(--mj-status-error);
      background: var(--mj-status-error-bg);
    }

    /* Processing status */
    .mkt-landing__status {
      margin-top: 12px;
    }
    .mkt-landing__status-bar {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 14px;
      background: var(--mj-status-info-bg);
      border: 1px solid var(--mj-status-info-border);
      border-radius: 6px;
      animation: mktStatusIn 0.2s ease;
    }
    .mkt-landing__status-bar--error {
      background: var(--mj-status-error-bg);
      border-color: var(--mj-status-error-border);
    }
    .mkt-landing__status-icon {
      flex-shrink: 0;
      font-size: 1rem;
      padding-top: 1px;
      color: var(--mj-brand-primary);
    }
    .mkt-landing__status-bar--error .mkt-landing__status-icon {
      color: var(--mj-status-error);
    }
    .mkt-landing__status-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .mkt-landing__status-message {
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--mj-status-info-text);
    }
    .mkt-landing__status-bar--error .mkt-landing__status-message {
      color: var(--mj-status-error-text);
    }
    .mkt-landing__status-error {
      font-size: 0.8rem;
      color: var(--mj-status-error);
    }
    @keyframes mktStatusIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* KPIs */
    .mkt-landing__kpis {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 12px;
      margin-bottom: 24px;
    }
    .mkt-kpi {
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 8px;
      padding: 14px 16px;
      text-align: center;
    }
    .mkt-kpi__value {
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 4px;
    }
    .mkt-kpi__label {
      font-size: 0.75rem;
      color: var(--mj-text-muted);
      font-weight: 500;
    }
    .mkt-kpi--sources .mkt-kpi__value { color: var(--mj-color-indigo-500); }
    .mkt-kpi--generated .mkt-kpi__value { color: var(--mj-color-violet-500); }
    .mkt-kpi--review .mkt-kpi__value { color: var(--mj-status-warning); }
    .mkt-kpi--published .mkt-kpi__value { color: var(--mj-status-success); }
    .mkt-kpi--total .mkt-kpi__value { color: var(--mj-text-muted); }
    .mkt-kpi--clickable {
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .mkt-kpi--clickable:hover {
      border-color: var(--mj-status-warning);
      box-shadow: 0 2px 8px color-mix(in srgb, var(--mj-status-warning) 15%, transparent);
    }

    /* Runs */
    .mkt-landing__section-title {
      margin: 0 0 12px;
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--mj-text-primary);
    }
    .mkt-landing__run-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .mkt-landing__empty {
      text-align: center;
      padding: 40px 20px;
      color: var(--mj-text-disabled);
    }
    .mkt-landing__empty i {
      font-size: 2rem;
      margin-bottom: 10px;
      display: block;
    }

    /* Dev simulate button */
    .mkt-landing__simulate-btn {
      padding: 6px 12px;
      border: 1px dashed var(--mj-color-violet-300, #c4b5fd);
      border-radius: 6px;
      background: var(--mj-color-violet-50, #f5f3ff);
      color: var(--mj-color-violet-600, #7c3aed);
      font-size: 0.8rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      transition: all 0.15s;
    }
    .mkt-landing__simulate-btn:hover {
      background: var(--mj-color-violet-100, #ede9fe);
      border-color: var(--mj-color-violet-500, #8b5cf6);
    }
    .mkt-landing__simulate-btn:active {
      transform: scale(0.97);
    }

    /* Advanced settings toggle */
    .mkt-landing__advanced-toggle {
      padding: 6px 12px;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      background: var(--mj-bg-surface);
      color: var(--mj-text-muted);
      font-size: 0.8rem;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      transition: all 0.15s;
    }
    .mkt-landing__advanced-toggle:hover {
      color: var(--mj-text-primary);
      border-color: var(--mj-brand-primary);
    }
    .mkt-landing__advanced-toggle--open {
      color: var(--mj-brand-primary);
      border-color: var(--mj-brand-primary);
      background: var(--mj-status-info-bg);
    }

    /* Advanced settings panel */
    .mkt-landing__advanced {
      display: flex;
      gap: 16px;
      margin-top: 10px;
      padding: 10px 12px;
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      animation: mktAdvancedIn 0.15s ease;
    }
    @keyframes mktAdvancedIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .mkt-landing__advanced-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .mkt-landing__advanced-row label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--mj-text-muted);
      white-space: nowrap;
    }
    .mkt-landing__advanced-row select {
      padding: 4px 8px;
      border: 1px solid var(--mj-border-default);
      border-radius: 4px;
      font-size: 0.8rem;
      font-family: inherit;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
      cursor: pointer;
    }
    .mkt-landing__advanced-row select:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Processing pipeline */
    .mkt-landing__pipeline {
      margin-top: 14px;
      padding: 14px 16px;
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 8px;
      animation: mktStatusIn 0.2s ease;
    }
    .mkt-pipeline-step {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 5px 0;
      transition: opacity 0.2s;
    }
    .mkt-pipeline-step--pending {
      opacity: 0.4;
    }
    .mkt-pipeline-step--done .mkt-pipeline-step__icon {
      color: var(--mj-status-success);
    }
    .mkt-pipeline-step--active .mkt-pipeline-step__icon {
      color: var(--mj-brand-primary);
    }
    .mkt-pipeline-step--pending .mkt-pipeline-step__icon {
      color: var(--mj-text-disabled);
    }
    .mkt-pipeline-step__icon {
      width: 18px;
      text-align: center;
      font-size: 0.85rem;
      flex-shrink: 0;
    }
    .mkt-pipeline-step__label {
      font-size: 0.82rem;
      font-weight: 500;
      color: var(--mj-text-primary);
    }
    .mkt-pipeline-step--pending .mkt-pipeline-step__label {
      color: var(--mj-text-disabled);
    }
    .mkt-pipeline-step--done .mkt-pipeline-step__label {
      color: var(--mj-text-muted);
    }
    .mkt-pipeline-elapsed {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--mj-border-default);
      font-size: 0.78rem;
      color: var(--mj-text-muted);
      font-variant-numeric: tabular-nums;
    }

    /* Button micro-interactions */
    .mkt-landing__generate-btn:active:not(:disabled) {
      transform: scale(0.97);
    }

    /* Keyboard shortcut hint */
    .mkt-landing__kbd {
      display: inline-flex;
      align-items: center;
      padding: 1px 5px;
      font-size: 0.65rem;
      font-family: inherit;
      font-weight: 400;
      background: color-mix(in srgb, var(--mj-text-inverse) 20%, transparent);
      border-radius: 3px;
      margin-left: 4px;
      opacity: 0.7;
      letter-spacing: 0.5px;
    }

    @media (max-width: 900px) {
      .mkt-landing__kpis {
        grid-template-columns: repeat(3, 1fr);
      }
    }
    @media (max-width: 600px) {
      .mkt-landing__kpis {
        grid-template-columns: repeat(2, 1fr);
      }
      .mkt-landing__hero-actions {
        flex-direction: column;
        align-items: stretch;
      }
      .mkt-landing__advanced {
        flex-direction: column;
        gap: 8px;
      }
    }
  `]
})
export class LandingComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() pipelineRuns: PipelineRun[] = [];
  @Input() blogs: Blog[] = [];
  @Input() contentSourceTypes: ContentSourceType[] = [];
  @Input() kpiSourcesIngested: number = 0;
  @Input() kpiPostsGenerated: number = 0;
  @Input() kpiAwaitingReview: number = 0;
  @Input() kpiPublished: number = 0;
  @Input() kpiTotalPosts: number = 0;
  @Input() isProcessing: boolean = false;
  @Input() processingStatus: string = '';
  @Input() processingError: string = '';
  @Input() processingSteps: ProcessingStep[] = [];
  @Input() genSettings: ContentGenSettings = { numPosts: 3, contentStyle: 'Blog Post', targetWordCount: 1200 };
  @Input() isDeveloperMode: boolean = false;
  @Input() newSourceContent: string = '';
  @Input() newSourceType: string = '';
  @Input() newSourceUrl: string = '';
  @Input() newSourceSpeaker: string = '';
  @Input() selectedFile: SelectedFile | null = null;

  @Output() newSourceContentChange = new EventEmitter<string>();
  @Output() newSourceTypeChange = new EventEmitter<string>();
  @Output() newSourceUrlChange = new EventEmitter<string>();
  @Output() newSourceSpeakerChange = new EventEmitter<string>();
  @Output() selectedFileChange = new EventEmitter<SelectedFile | null>();
  @Output() genSettingsChange = new EventEmitter<ContentGenSettings>();
  @Output() createContent = new EventEmitter<void>();
  @Output() simulateRun = new EventEmitter<void>();
  @Output() selectRun = new EventEmitter<number>();
  @Output() openReviewQueue = new EventEmitter<void>();

  isDragOver = false;
  activeTab: 'paste' | 'upload' = 'paste';
  advancedOpen = false;

  contentStyles = ['Blog Post', 'Listicle', 'How-To Guide', 'Thought Leadership', 'Case Study', 'News Brief'];
  wordCounts = [
    { label: '~500 (Short)', value: 500 },
    { label: '~800 (Medium)', value: 800 },
    { label: '~1200 (Standard)', value: 1200 },
    { label: '~1500 (Long)', value: 1500 },
    { label: '~2000 (Deep Dive)', value: 2000 },
  ];

  private readonly urlTypes = ['URL', 'RSS Feed'];
  private readonly docTypes = ['Document'];
  private readonly podcastTypes = ['Podcast Transcript', 'Webinar Recording'];

  get showTextarea(): boolean {
    return !this.newSourceType || this.podcastTypes.includes(this.newSourceType);
  }

  get showUrlInput(): boolean {
    return this.urlTypes.includes(this.newSourceType);
  }

  get showFileUpload(): boolean {
    return this.docTypes.includes(this.newSourceType);
  }

  get isPodcastOrWebinar(): boolean {
    return this.podcastTypes.includes(this.newSourceType);
  }

  get textareaPlaceholder(): string {
    if (this.isPodcastOrWebinar) {
      return 'Paste your podcast or webinar transcript here...';
    }
    return 'Paste a transcript, article, or any source content here...';
  }

  get fileAccept(): string {
    return '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';
  }

  get canGenerate(): boolean {
    if (!this.newSourceType) {
      return this.newSourceContent.trim().length > 0;
    }
    if (this.urlTypes.includes(this.newSourceType)) {
      return this.newSourceUrl.trim().length > 0;
    }
    if (this.docTypes.includes(this.newSourceType)) {
      return this.selectedFile != null;
    }
    if (this.podcastTypes.includes(this.newSourceType)) {
      return this.newSourceContent.trim().length > 0;
    }
    return this.newSourceContent.trim().length > 0;
  }

  onSourceTypeChange(type: string): void {
    this.activeTab = 'paste';
    this.newSourceTypeChange.emit(type);
  }

  onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      if (this.canGenerate && !this.isProcessing) {
        this.createContent.emit();
      }
    }
  }

  updateSetting(key: string, value: any): void {
    this.genSettings = { ...this.genSettings, [key]: value };
    this.genSettingsChange.emit(this.genSettings);
  }

  // --- File handling ---

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFileChange.emit(null);
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private processFile(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the data:...;base64, prefix
      const base64 = dataUrl.split(',')[1] || '';
      this.selectedFileChange.emit({
        name: file.name,
        base64,
        mimeType: file.type,
        size: file.size
      });
    };
    reader.readAsDataURL(file);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
